/**
 * The ⓘ "how this works" button (Corbin, 10/22: "we can't add more mud to the
 * water"). Any step that could confuse someone gets one. Tapping it opens a
 * short, plain-words how-to: numbered steps, each with a screenshot of the
 * real screen marked up with an orange ring on what to tap and a tag saying
 * what it does. The pictures live in client/public/how-to/.
 *
 * It never changes anything on the screen behind it; "Got it" just closes it.
 */
import { useState, type ReactNode } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface HowStep {
    title: string;
    text: ReactNode;
    /** File name in /how-to/ (an annotated screenshot). */
    picture?: string;
    /** What the picture shows, for screen readers. */
    alt?: string;
}

export function HowItWorks({
    title,
    steps,
    className,
    testId,
}: {
    /** "How … works" — shown as the sheet's title and the button's label. */
    title: string;
    steps: HowStep[];
    className?: string;
    testId?: string;
}) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(true);
                }}
                aria-label={title}
                title={title}
                data-testid={testId ?? "how-it-works"}
                className={cn(
                    "inline-grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 border-blue-600 bg-background font-serif text-[12px] font-extrabold leading-none text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-300 dark:hover:bg-blue-950/40",
                    className,
                )}
            >
                i
            </button>
            <Dialog open={open} onClose={() => setOpen(false)} title={title}>
                <ol className="space-y-4" data-testid="how-it-works-steps">
                    {steps.map((s, i) => (
                        <li key={s.title} className="space-y-1.5">
                            <p className="flex items-baseline gap-2 text-sm font-semibold">
                                <span className="inline-grid h-6 w-6 shrink-0 place-items-center rounded-full bg-orange-700 text-xs font-bold text-white">
                                    {i + 1}
                                </span>
                                {s.title}
                            </p>
                            <div className="text-sm text-foreground/80">{s.text}</div>
                            {s.picture && (
                                <img
                                    src={`/how-to/${s.picture}`}
                                    alt={s.alt ?? s.title}
                                    loading="lazy"
                                    className="w-full rounded-md border border-border"
                                />
                            )}
                        </li>
                    ))}
                </ol>
                <div className="flex justify-end pt-4">
                    <Button onClick={() => setOpen(false)}>Got it</Button>
                </div>
            </Dialog>
        </>
    );
}
